import { createTheme, Theme } from '@mui/material/styles';
import { COLOR_SCHEMES, ColorSchemeType } from './color-schemes';

/**
 * Creates a Material-UI theme based on the selected color scheme
 * @param colorScheme - The color scheme key to use
 * @returns Material-UI Theme object
 */
export const createAppTheme = (colorScheme: ColorSchemeType = 'blue'): Theme => {
  const scheme = COLOR_SCHEMES[colorScheme];

  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: scheme.primary,
        dark: scheme.secondary,
        light: scheme.accent,
      },
      secondary: {
        main: scheme.secondary,
        dark: scheme.secondary,
        light: scheme.accent,
      },
      background: {
        default: scheme.background.default,
        paper: scheme.background.paper,
      },
      text: {
        primary: scheme.text.primary,
        secondary: scheme.text.secondary,
      },
    },
    typography: {
      fontFamily: "'Nunito', sans-serif",
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        lineHeight: 1.2,
        '@media (max-width:600px)': {
          fontSize: '2rem',
        },
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.3,
        '@media (max-width:600px)': {
          fontSize: '1.75rem',
        },
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.4,
        '@media (max-width:600px)': {
          fontSize: '1.5rem',
        },
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
        '@media (max-width:600px)': {
          fontSize: '1.25rem',
        },
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.7,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 8,
    },
    spacing: 8,
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 24px',
            fontSize: '1rem',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
            borderRadius: 0,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          elevation1: {
            boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          },
          elevation2: {
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          },
          elevation3: {
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            '@media (min-width: 1200px)': {
              maxWidth: '1280px',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&:before': {
              display: 'none',
            },
            marginBottom: '8px',
          },
        },
      },
    },
  });
};
